import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLink, IPText } from 'baseComponents';
import { IPIcon } from 'baseComponents/Icon/IPIcon';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import { getListIcon, getProviderIcon } from 'components/Links/Helpers/LinkIcons';
import AddNewListPanel from 'components/lists/AddNewListPanel';
import { CategoryMenuButton, isEligibleCategory } from 'components/OutputLocations/CategoryMenu';
import OutputFolderPicker, { canPickOutputFolder } from 'components/OutputLocations/OutputFolderPicker';
import { createCategory } from 'components/OutputLocations/outputLocationActions';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { getProviderName } from 'models/documentProvider';
import DocumentOutputLocation, { DocumentOutputPurpose, kpiAttachmentProviders } from 'models/documentOutputLocation';
import { ReportSaveLocation } from 'models/library/reportSaveLocation';
import ResourceList from 'models/resourceList';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ISaveToLocationDialogProps {
  // the name the file is saved under
  fileName: string;
  // the default location the server resolved (the preview); undefined: no default, the picker shows at once
  location?: ReportSaveLocation;
  // the purpose whose row is the default; it decides which categories the picker offers
  purpose?: DocumentOutputPurpose;
  // the reports storage is switched off: no default is offered, the picker shows at once
  reportsStorageOff?: boolean;
  isSaving: boolean;
  // no listId: the default location; a listId: the user's choice for this save only, the folder undefined for its root
  onSave: (listId: number | undefined, folderDriveItemId: string | undefined) => void | Promise<void>;
  onClose: () => void;
}

// Saves a file to the library (PLAN-069): the default location is shown before the save, and "Choose another
// location" picks a category and a folder for this save only; the default itself is never changed here. The host
// does the save and shows its errors.
const SaveToLocationDialog = (props: ISaveToLocationDialogProps) => {
  const { t } = useTranslation(['copilot', 'outputLocations', 'exceptions']);
  const appContext = useContext(AppContext);
  const hasDefault = props.location !== undefined && !props.reportsStorageOff;
  const [useOverride, setUseOverride] = useState<boolean>(!hasDefault);
  const [listsLoaded, setListsLoaded] = useState<boolean>(false);
  const [list, setList] = useState<ResourceList | undefined>(undefined);
  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const [folderName, setFolderName] = useState<string | undefined>(undefined);
  const [showFolderPicker, setShowFolderPicker] = useState<boolean>(false);
  const [addCategory, setAddCategory] = useState<boolean>(false);

  // the categories the menu offers: the upload providers, the set the catalogue gives every upload purpose
  const menuLocation = useMemo((): DocumentOutputLocation => {
    const output = new DocumentOutputLocation();
    output.purpose = props.purpose ?? DocumentOutputPurpose.GeneralReports;
    output.allowedProviders = kpiAttachmentProviders;

    return output;
  }, [props.purpose]);

  // the list cache is lazy: the menu reads it once it is loaded
  useEffect(() => {
    const load = async () => {
      try {
        await appContext.globalDataCache.lists.getItems();
        setListsLoaded(true);
      } catch (err) {
        appContext.setError(err);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // a preview that arrives after the dialog opened shows the default; without one the picker shows at once
  useEffect(() => {
    setUseOverride(!hasDefault);
  }, [hasDefault]);

  const pickList = (picked: ResourceList | undefined) => {
    setList(picked);
    setFolderId(undefined);
    setFolderName(undefined);
  };

  const onCategoryCreated = async (created: ResourceList) => {
    try {
      const saved = await createCategory(appContext, created);
      if (isEligibleCategory(menuLocation, saved)) {
        pickList(saved);
      } else {
        appContext.showNotification(t('exceptions:OutputLocation.NotADocumentLibrary'), true, false, true);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  const getDefaultLine = (location: ReportSaveLocation): string => {
    const path = [location.listName, location.folderName, location.fileName].filter((part) => !!part).join(' / ');

    return t('copilot:Chat.File.SaveLocation', { location: path, provider: getProviderName(location.providerType) });
  };

  const getDefaultIcon = (location: ReportSaveLocation) => {
    const cached = appContext.globalDataCache.lists.get(location.listId);
    if (cached) return getListIcon(cached);

    return getProviderIcon(location.providerType) ?? { iconName: 'DocLibrary' };
  };

  const save = async () => {
    if (useOverride) {
      if (!list) return;
      await props.onSave(list.listId, folderId);
    } else {
      await props.onSave(undefined, undefined);
    }
  };

  const renderDefault = (location: ReportSaveLocation) => (
    <IPStack tokens={globalStackTokensGapSmall}>
      <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
        <IPIcon {...getDefaultIcon(location)} />
        <IPText>{getDefaultLine(location)}</IPText>
      </IPStack>
      <IPLink disabled={props.isSaving} onClick={() => setUseOverride(true)}>
        {t('copilot:Chat.File.ChooseOtherLocation')}
      </IPLink>
    </IPStack>
  );

  const renderOverride = () => (
    <IPStack tokens={globalStackTokensGapMedium}>
      {!hasDefault && (
        <IPText>
          {props.reportsStorageOff
            ? t('copilot:Chat.File.ReportsStorageOff')
            : t('copilot:Chat.File.NoDefaultLocation')}
        </IPText>
      )}
      <IPStack.Item>
        <CategoryMenuButton
          location={menuLocation}
          selectedListId={list?.listId}
          disabled={!listsLoaded || props.isSaving}
          onPick={(picked: ResourceList) => pickList(picked)}
          onCreateNew={() => setAddCategory(true)}
        />
      </IPStack.Item>
      {list && (
        <IPStack horizontal verticalAlign="center" wrap tokens={globalStackTokensGapMedium}>
          <IPDefaultButton
            text={t('outputLocations:Folder.Select')}
            disabled={!canPickOutputFolder(list) || props.isSaving}
            onClick={() => setShowFolderPicker(true)}
          />
          <IPText>
            {folderName
              ? t('outputLocations:Folder.Line', { folder: folderName })
              : t('outputLocations:Folder.RootLine')}
          </IPText>
        </IPStack>
      )}
      {hasDefault && (
        <IPLink
          disabled={props.isSaving}
          onClick={() => {
            pickList(undefined);
            setUseOverride(false);
          }}
        >
          {t('copilot:Chat.File.UseDefaultLocation')}
        </IPLink>
      )}
    </IPStack>
  );

  return (
    <>
      <GenericModal
        isOpen={true}
        title={t('copilot:Chat.File.PickerTitle')}
        subTitle={props.fileName}
        onClose={props.onClose}
        onSave={save}
        keepOpenAfterSave
        isActionPending={props.isSaving}
        saveDisabled={props.isSaving || (useOverride && !list)}
        saveButtonText={t('copilot:Chat.File.SaveToLibrary')}
        width={560}
        maxWidth={'min(90vw, 560px)'}
      >
        <IPStack styles={{ root: { paddingTop: 10 } }}>
          {useOverride || !props.location ? renderOverride() : renderDefault(props.location)}
        </IPStack>
      </GenericModal>
      {showFolderPicker && list && (
        <OutputFolderPicker
          list={list}
          onSelect={(id, name) => {
            setFolderId(id);
            setFolderName(name);
          }}
          onClose={() => setShowFolderPicker(false)}
        />
      )}
      {addCategory && (
        <AddNewListPanel
          isOpen={addCategory}
          renderAsPanel={false}
          onDismiss={() => setAddCategory(false)}
          onAddNew={(created: ResourceList) => onCategoryCreated(created)}
        />
      )}
    </>
  );
};

export default SaveToLocationDialog;
